import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Eye } from "lucide-react";
import { Avatar } from "@/components/common/Avatar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CreateStoryDialog } from "./CreateStoryDialog";
import { StoryViewer } from "./StoryViewer";

function Ring({ unseen, children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex w-16 shrink-0 flex-col items-center gap-1"
      data-testid={testid}
    >
      <span className={cn("rounded-full p-[2px]", unseen ? "bloq-gradient" : "bg-white/15")}>
        <span className="block rounded-full border-2 border-bloq-s1">{children}</span>
      </span>
    </button>
  );
}

export function StoriesBar() {
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [viewer, setViewer] = useState(null);

  const { data } = useQuery({
    queryKey: ["stories-feed"],
    queryFn: api.storiesFeed,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const mine = data?.mine;
  const items = data?.items || [];
  const hasMine = !!(mine && (mine.count ?? 0) > 0);

  // "N friends viewed" nudge — sum views across my active stories (drives daily returns).
  const { data: myStories } = useQuery({
    queryKey: ["user-stories", user?.id],
    queryFn: () => api.userStories(user.id),
    enabled: hasMine && !!user?.id,
    staleTime: 30000,
  });
  const totalViews = (myStories?.stories || []).reduce((n, s) => n + (s.view_count || 0), 0);

  const groups = [
    ...(hasMine ? [{ user: mine.user || user, mine: true }] : []),
    ...items.map((it) => ({ user: it.user })),
  ];

  const openAt = (idx) => setViewer({ groups, index: idx });

  // Hide the bar entirely only when there is truly nothing to show AND we still show "Your story" to seed creation.
  return (
    <div className="mb-3 flex items-center gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="stories-bar">
      {/* Your story */}
      <div className="flex w-16 shrink-0 flex-col items-center gap-1">
        <button
          onClick={() => (hasMine ? openAt(0) : setCreateOpen(true))}
          className="relative"
          data-testid="story-your-ring"
        >
          <span className={cn("block rounded-full p-[2px]", hasMine ? "bloq-gradient" : "bg-white/15")}>
            <span className="block rounded-full border-2 border-bloq-s1">
              <Avatar user={user} size="md" flair={false} />
            </span>
          </span>
          <span
            onClick={(e) => { e.stopPropagation(); setCreateOpen(true); }}
            className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-bloq-s1 bg-bloq-mint text-bloq-bg"
            data-testid="story-add-btn"
          >
            <Plus className="h-3.5 w-3.5" />
          </span>
        </button>
        <span className={cn("flex max-w-[60px] items-center justify-center gap-0.5 truncate text-[11px]", totalViews > 0 ? "font-semibold text-bloq-mint" : "text-bloq-muted")} data-testid="story-your-label">
          {totalViews > 0 ? <><Eye className="h-3 w-3 shrink-0" /> {totalViews}</> : "Your story"}
        </span>
      </div>

      {items.map((it, i) => (
        <div key={it.user.id} className="flex w-16 shrink-0 flex-col items-center gap-1">
          <Ring
            unseen={it.unseen ?? true}
            onClick={() => openAt((hasMine ? 1 : 0) + i)}
            testid={`story-ring-${it.user.username}`}
          >
            <Avatar user={it.user} size="md" flair={false} />
          </Ring>
          <span className="max-w-[60px] truncate text-[11px] text-white/80">{it.user.display_name?.split(" ")[0] || it.user.username}</span>
        </div>
      ))}

      <CreateStoryDialog open={createOpen} onOpenChange={setCreateOpen} />
      {viewer && <StoryViewer groups={viewer.groups} startIndex={viewer.index} onClose={() => setViewer(null)} />}
    </div>
  );
}
